import React, { FunctionComponent } from 'react';

import { connect, useDispatch } from 'react-redux';

import { Translate } from 'react-localize-redux';

import AppState from '@webapp/store/state';
import { trackList } from '@webapp/store/tracks/selectors';

import { normalizeTrackName } from '@webapp/utils/normalizers';

import SimpleTrack from '@common/types/release/simple-track';
import SimpleArtist from '@common/types/artist/simple-artist';

import TabsNav, { TabProp } from '@components/tabs-nav';

import { useLocation } from 'react-router';
import { push } from 'connected-react-router';
import styled from 'styled-components';

import * as releaseValidators from '@webapp/utils/validators/release';

interface ReleaseMenuMobileProps {
  className?: string;
  activeArtist?: SimpleArtist;
  releaseId?: string;
  tracks: Partial<SimpleTrack>[];
  titles: { id?: string; title: string }[];

  isTitleSectionValid: boolean;
  isDetailsSectionValid: boolean;
  isDatesSectionValid: boolean;
}

type ReleaseTabs = 'title' | 'details' | 'dates';

const initialTab = 0;

const mapStateToProps = (state: AppState) => {
  const tracks = trackList(
    state.tracks.tracks,
    state.release.release?.id as string
  );
  return {
    releaseId: state.release.release?.id,
    activeArtist: state.artist.active,
    isTitleSectionValid: releaseValidators.titleSection(state),
    isDetailsSectionValid: releaseValidators.detailsSection(state),
    isDatesSectionValid: releaseValidators.datesSection(state),
    tracks,
    titles: tracks.map((track) => ({
      id: track.id,
      title: normalizeTrackName(
        {
          ...track,
          trackVersion:
            track.trackVersion === 'Original' ? '' : track.trackVersion,
        },
        state
      ),
    })),
  };
};

const ReleaseMenuMobile: FunctionComponent<ReleaseMenuMobileProps> = ({
  className,
  releaseId,
  activeArtist,
  tracks,
  titles,
  isTitleSectionValid,
  isDetailsSectionValid,
  isDatesSectionValid,
}) => {
  const location = useLocation();
  const artistId = activeArtist?.id;

  const getRoute = (section: string) => {
    const propsReleaseId = releaseId;

    return (
      '/' +
      ['release', artistId, propsReleaseId, section]
        .filter((f) => !!f)
        .join('/')
    );
  };

  const getTrackRoute = (trackId: string) => {
    return (
      '/' +
      ['release', artistId, releaseId, 'track', trackId]
        .filter((f) => !!f)
        .join('/')
    );
  };

  const isDone = React.useCallback(
    (section: string) => {
      switch (section) {
        case 'title':
          return isTitleSectionValid;
        case 'details':
          return isDetailsSectionValid;
        case 'dates':
          return isDatesSectionValid;
      } // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [isTitleSectionValid, isDetailsSectionValid, isDatesSectionValid]
  );

  const mergedTracks = tracks.map((track) => {
    const title = titles.find((t) => t.id === track.id);
    const id = track.id || '';

    const maxCharacters = 40;

    const textShortener = (label: string) => {
      if (label.length > maxCharacters) {
        return `${label.slice(0, maxCharacters)}...`;
      }
    };

    const getTrackLabel = () => {
      if (title?.title) {
        if (title?.title.length > 24) {
          return textShortener(title?.title);
        } else {
          return title?.title;
        }
      } else if (track.title) {
        if (track.title.length > 24) {
          return textShortener(track.title);
        } else {
          return track.title;
        }
      }
    };

    return {
      label: getTrackLabel() || '',
      key: id,
      isTrack: true,
      disabled: !(isDone('dates') && releaseId),
    };
  });

  const tabsData: TabProp[] = React.useMemo(
    () => [
      {
        label: <Translate id="RELEASE.SIDEBAR.LIST.COVER_MUSIC" />,
        key: 'title',
        disabled: false,
      },
      {
        label: <Translate id="RELEASE.SIDEBAR.LIST.NAMES_GENRES" />,
        key: 'details',
        disabled: !(isDone('title') && releaseId),
      },
      {
        label: <Translate id="RELEASE.SIDEBAR.LIST.DATES_STORES" />,
        key: 'dates',
        disabled: !(isDone('details') && releaseId),
      },
      ...mergedTracks,
    ],
    [mergedTracks, isDone, releaseId]
  );

  const [activeTab, setActiveTab] = React.useState(tabsData[initialTab].key);

  const getActiveTab = (url: string) => {
    const urlArray = url.split('/');
    const lastUrl = urlArray[urlArray.length - 1];
    return lastUrl;
  };

  React.useEffect(() => {
    if (getActiveTab(location.pathname) === 'title') {
      setActiveTab(tabsData[0].key);
    } else if (getActiveTab(location.pathname) === 'details') {
      setActiveTab(tabsData[1].key);
    } else if (getActiveTab(location.pathname) === 'dates') {
      setActiveTab(tabsData[2].key);
    } else if (titles) {
      const activeTrack = titles.find(
        (t) => t.id === getActiveTab(location.pathname)
      );
      if (activeTrack) {
        setActiveTab(activeTrack.id as string);
      }
    } else {
      setActiveTab(tabsData[0].key);
    }
  }, [tabsData, titles, location.pathname]);

  const dispatch = useDispatch();

  const handleTabClick = (value: ReleaseTabs, isTrack?: boolean) => {
    setActiveTab(value);
    if (isTrack) {
      dispatch(push(getTrackRoute(value)));
    } else {
      dispatch(push(getRoute(value)));
    }
  };

  return (
    <TabsNav
      className={className || ''}
      tabs={tabsData}
      currentTab={tabsData.findIndex((tab) => tab.key === activeTab)}
      onChange={(tab) => handleTabClick(tab.key as ReleaseTabs, tab.isTrack)}
      bigFont
      activeBigFont
      overflow
    />
  );
};

export default connect(mapStateToProps)(styled(ReleaseMenuMobile)`
  padding-inline: 24px;
`);
